import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { cpuOpponentLabel } from '../../data/address-config';
import { timeAgo } from '../../utils/format-time';
import { AddressColorPipe } from '../../pipes/address-color.pipe';
import { RecentBattlesService } from '../../services/recent-battles.service';
import { ServerService } from '../../services/server.service';
import { formatPlayerName } from '../../services/utils.service';
import { Wallet } from '../../services/wallet';
import { RecentBattleEntry, RecentBattleSide } from '../../types/server';
import { MonStaticSpriteComponent } from '../mon-static-sprite/mon-static-sprite.component';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';

@Component({
  selector: 'app-recent-battles',
  imports: [
    RouterLink,
    AddressColorPipe,
    PixelCreatureComponent,
    MonStaticSpriteComponent,
  ],
  templateUrl: './recent-battles.component.html',
  styleUrl: './recent-battles.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecentBattlesComponent implements OnInit {
  private readonly recent = inject(RecentBattlesService);
  private readonly server = inject(ServerService);
  private readonly wallet = inject(Wallet);
  private readonly destroyRef = inject(DestroyRef);

  readonly battles = this.recent.battles;

  readonly carousel = input<boolean>(false);
  private readonly scroller = viewChild<ElementRef<HTMLDivElement>>('scroller');
  readonly activeIndex = signal(0);

  private cardStep(el: HTMLElement): number {
    const card = el.querySelector('.rb-card') as HTMLElement | null;
    return card ? card.offsetWidth + 8 : el.clientWidth;
  }

  step(dir: -1 | 1): void {
    const el = this.scroller()?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: dir * this.cardStep(el), behavior: 'smooth' });
  }

  goTo(index: number): void {
    const el = this.scroller()?.nativeElement;
    if (!el) return;
    el.scrollTo({ left: index * this.cardStep(el), behavior: 'smooth' });
  }

  onScroll(): void {
    const el = this.scroller()?.nativeElement;
    if (!el) return;
    this.activeIndex.set(Math.max(0, Math.round(el.scrollLeft / this.cardStep(el))));
  }

  ngOnInit(): void {
    void this.recent.refresh();
    // The lean BATTLE_COMPLETE broadcast is a "something changed" signal here,
    // not the data source — re-pull the list whenever a battle finishes.
    this.server.battleLogEvent
      .pipe(
        filter((e) => e.type === 'BATTLE_COMPLETE'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => void this.recent.refresh());
  }

  involvesMe(b: RecentBattleEntry): boolean {
    return this.isMe(b.winner) || this.isMe(b.loser);
  }

  isMe(side: RecentBattleSide): boolean {
    return side.address.toLowerCase() === this.wallet.activeAddress();
  }

  displayName(b: RecentBattleEntry, side: RecentBattleSide): string {
    // Every CPU difficulty shares one on-chain address, so the tier label comes
    // from the server-reported difficulty, not the (always-BetterCPU) address.
    if (side.isCpu) {
      return cpuOpponentLabel(b.cpuDifficulty);
    }
    return formatPlayerName(this.server.resolveIdentity(side));
  }

  relativeTime(ts: number): string {
    return timeAgo(ts, ' ago');
  }
}
